import { GreetingMessage } from "@/components/ui/greeting-message/GreetingMessage.tsx";
import { HomeIllustration } from "@/components/ui/illustrations/home-illustration/HomeIllustration.tsx";
import { SuggestedPrompts } from "@/components/ui/suggested-prompt/SuggestedPrompts.tsx";
import { Div, Flex } from "@hopper-ui/components";
import {
    ConversationInputForm,
    FileDropZone,
    Footer,
    PageLoading,
    SlackCallout,
    useCreateConversation,
    useFilesDragAndDrop,
    useIsCreateConversationMutating,
    useOAuthToast,
    useUploadFiles
} from "@workleap-ai/shared";
import { Suspense, useCallback } from "react";

const HomePage = () => {
    return (
        <Suspense fallback={<PageLoading />}>
            <HomePageChild />
        </Suspense>
    );
};

const HomePageChild = () => {
    const { handleFileSelected, files, removeFile, getOrCreateConversationId } = useUploadFiles();
    const { createConversation } = useCreateConversation();
    const isMutating = useIsCreateConversationMutating();
    const {
        isDropZoneActive,
        handleDragEnter,
        handleDragOver,
        handleDragLeave,
        handleDrop
    } = useFilesDragAndDrop(handleFileSelected, isMutating);

    // Handle OAuth query parameters and show toasts
    useOAuthToast();

    const onSubmit = useCallback(async (value: string) => {
        const ensuredConversationId = await getOrCreateConversationId();

        if (!ensuredConversationId) {
            // Conversation creation failed; the error toast was already shown.
            return;
        }

        createConversation(value, { files, existingConversationId: ensuredConversationId });
    }, [createConversation, files, getOrCreateConversationId]);

    return (
        <Flex
            direction="column"
            overflowY="auto"
            paddingX={{
                base: "inset-md",
                sm: "inset-lg"
            }}
            paddingTop={{
                base: "inset-md",
                sm: "inset-lg"
            }}
            paddingBottom={{
                base: "core_0",
                sm: "inset-lg"
            }}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            position="relative"
            height="100%"
        >
            <Flex
                direction="column"
                justifyContent="center"
                alignItems="center"
                grow={1}
            >
                <Flex
                    direction="column"
                    alignItems="center"
                    gap="inline-lg"
                    UNSAFE_maxWidth="var(--wai-page-max-width)"
                    marginX="auto"
                >
                    <HomeIllustration />
                    <GreetingMessage />
                </Flex>
                <Div
                    width="100%"
                    UNSAFE_maxWidth="var(--wai-page-max-width)"
                    UNSAFE_marginY={{
                        base: "inline-lg",
                        sm: "2.5rem"
                    }}
                    marginX="auto"
                    marginTop="inline-md"
                >
                    <SlackCallout />
                    <ConversationInputForm
                        files={files}
                        isMutating={isMutating}
                        autofocus
                        padding="inset-md"
                        handleFileSelected={handleFileSelected}
                        removeFile={removeFile}
                        onSubmit={onSubmit}
                        showUpgradeBanner
                    />

                </Div>
                <SuggestedPrompts />
            </Flex>
            <Footer />
            {isDropZoneActive && <FileDropZone />}
        </Flex>
    );
};

export default HomePage;
